import { useCallback, useMemo } from "react";
import { noop } from "@common/helpers/function";
import { GetCounterpartiesQuery } from "@common/schemas/counterparty.schema";
import {
    CounterpartyDirectoryView,
    CounterpartyDirectoryViewSchema,
} from "@common/types/views/counterpartyDirectoryView";
import { DirectoryContext } from "@components/settings/Directory/Directory.context";
import { DirectoryTable } from "@components/settings/Directory/DirectoryTable";
import { toDirectoryFilters } from "@components/tasks/TaskBody/Counterparties/counterpartiesTaskView";
import { useCounterpartiesDirectoryContext } from "@components/tasks/TaskBody/Counterparties/useCounterpartiesDirectoryContext";
import { useCounterpartiesTaskRows } from "@components/tasks/TaskBody/Counterparties/useCounterpartiesTaskRows";
import {
    TemplateTaskDataTable,
    TemplateTaskTableProps,
} from "./TemplateTaskDataTable";
import { ResolvedTaskView } from "./useResolvedTaskView";

interface Props {
    templateView: CounterpartyDirectoryView;
    onTemplateViewChange: (view: CounterpartyDirectoryView) => void;
    resolvedView: ResolvedTaskView;
    workspaceId: string | null;
    extraQueryFilters?: Partial<GetCounterpartiesQuery>;
}

interface ResolvedCounterpartiesTableProps extends TemplateTaskTableProps<CounterpartyDirectoryView> {
    extraQueryFilters?: Partial<GetCounterpartiesQuery>;
}

const ResolvedCounterpartiesTable = ({
    view,
    onViewChange,
    emptyView,
    extraQueryFilters,
}: ResolvedCounterpartiesTableProps) => {
    const scope = useMemo(
        () => toDirectoryFilters(view.filters),
        [view.filters],
    );
    const { contextValue, counterpartySort, expanded, setExpanded, resetRows } =
        useCounterpartiesDirectoryContext({
            view,
            onViewChange,
            scope,
            onScopeChange: noop,
        });
    const { counterparties } = useCounterpartiesTaskRows({
        queryScopeKey: "template-preview",
        scope,
        counterpartySort,
        viewFilters: view.filters,
        resetRows,
        extraQueryFilters,
    });

    return (
        <DirectoryContext.Provider value={contextValue}>
            <DirectoryTable
                counterpartiesData={counterparties.data}
                isLoading={counterparties.isLoading}
                totalCounterparties={counterparties.data?.data.length ?? 0}
                expanded={expanded}
                onExpandedChange={setExpanded}
                shouldExcludeNoCounterpartyRow
                emptyView={emptyView}
            />
        </DirectoryContext.Provider>
    );
};

export const TemplateCounterpartiesDataTable = ({
    extraQueryFilters,
    ...props
}: Props) => {
    const renderCounterpartiesTable = useCallback(
        (tableProps: TemplateTaskTableProps<CounterpartyDirectoryView>) => (
            <ResolvedCounterpartiesTable
                {...tableProps}
                extraQueryFilters={extraQueryFilters}
            />
        ),
        [extraQueryFilters],
    );

    return (
        <TemplateTaskDataTable
            {...props}
            viewSchema={CounterpartyDirectoryViewSchema}
            rowsLabel="counterparties"
            renderTable={renderCounterpartiesTable}
        />
    );
};
